import { GAS_PRICE_ERROR_REEMIT_INTERVAL } from '../../consts/intervals'
import { ErrorRef } from '../../interfaces/eventEmitter'
import { Network } from '../../interfaces/network'
import { RPCProvider } from '../../interfaces/provider'
import { BaseAccount } from '../../libs/account/BaseAccount'
import { decodeError } from '../../libs/errorDecoder'
import { ErrorType } from '../../libs/errorDecoder/types'
import { gasPriceToBundlerFormat, getGasPriceRecommendations } from '../../libs/gasPrice/gasPrice'
import { getAvailableBunlders } from '../../services/bundlers/getBundler'
import { GasSpeeds } from '../../services/bundlers/types'
import { EstimationController } from '../estimation/estimation'
import EventEmitter from '../eventEmitter/eventEmitter'

export class GasPriceController extends EventEmitter {
  #network: Network

  #provider: RPCProvider

  #baseAccount: BaseAccount

  #getSignAccountOpState: () => {
    estimation: EstimationController
    readyToSign: boolean
    stopRefetching: boolean
  }

  gasPrices?: GasSpeeds

  /**
   * Timestamp of the last successful gas price update
   * TODO: Merge them into a single structure
   * {
   *  gasPrices: GasSpeeds
   *  updatedAt: number
   * }
   */
  updatedAt?: number

  /**
   * If the bundler estimation succeeds successfully, we don't want
   * to use the estimation from the gas price controller unless
   * explicitly called from the signAccountOp.
   * */
  areGasPricesUsedFromBundlerEstimation: boolean = false

  /**
   * Timestamp of the last gas price failure that was surfaced to the user.
   *
   * `fetch()` runs on a 12s interval for as long as a SignAccountOp is alive, and
   * the "we couldn't retrieve the network fee" guard below only goes quiet once a
   * fetch has succeeded at least once. On a chain whose RPC never serves the fee
   * data (PulseChain, historically) that means one visible error every 12s — and
   * since the toast lives for 12s too, the user sees what looks like a permanent
   * banner that only clears when the service worker dies. Surface the first
   * failure, then throttle the visible ones to GAS_PRICE_ERROR_REEMIT_INTERVAL.
   */
  #lastVisibleErrorAt?: number

  constructor(
    network: Network,
    provider: RPCProvider,
    baseAccount: BaseAccount,
    getSignAccountOpState: () => {
      estimation: EstimationController
      readyToSign: boolean
      stopRefetching: boolean
    }
  ) {
    super()
    this.#network = network
    this.#provider = provider
    this.#baseAccount = baseAccount
    this.#getSignAccountOpState = getSignAccountOpState
  }

  /**
   * Downgrades a repeated gas price failure to 'silent' so the recurring fetch
   * can't spam the same toast forever. The first failure (and one every
   * GAS_PRICE_ERROR_REEMIT_INTERVAL after it) is still shown at the requested
   * level, so the user learns about the problem without being buried by it.
   */
  #getFailureEmitOptions(
    requestedLevel: ErrorRef['level']
  ): Pick<ErrorRef, 'level' | 'sendCrashReport'> {
    if (requestedLevel === 'silent') return { level: 'silent' }

    const now = Date.now()
    if (
      this.#lastVisibleErrorAt &&
      now - this.#lastVisibleErrorAt < GAS_PRICE_ERROR_REEMIT_INTERVAL
    ) {
      // Also skip the crash report: the first failure of this streak already
      // reported it, and one report per 12s per broken chain is pure noise.
      return { level: 'silent', sendCrashReport: false }
    }

    this.#lastVisibleErrorAt = now
    return { level: requestedLevel }
  }

  async fetch(emitLevelOnFailure: ErrorRef['level'] = 'silent') {
    if (this.areGasPricesUsedFromBundlerEstimation) return

    // give priority to the bundler as it's faster and more accurate
    // we ask the bundler only when the estimation is not supported by the account
    // it is counter intuitive but the logic if the account supports the bundler
    // estimate, it would fetch the gas price from the bundler estimation itself,
    // therefore not being required here
    const availableBundlers = getAvailableBunlders(this.#network)
    if (availableBundlers.length && !this.#baseAccount.supportsBundlerEstimation()) {
      let timeoutId
      const bundlerGasPrices = await Promise.race([
        // Promise.any because we want the first success, ignoring errors
        // basically, call all the available bundlers on the network for
        // gas prices and take the results from the quickest one.
        // Also, limit it to 6s - if slower than that, we should fallback
        // to our own mechanism
        Promise.any(availableBundlers.map((bundler) => bundler.fetchGasPrices(this.#network))),
        new Promise((_resolve, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('bundler gas price fetch fail, request too slow')),
            6000
          )
        })
      ]).catch(() => {
        console.error('Failed fetching bundler gas prices from the gasPrice lib')
        return null
      })
      clearTimeout(timeoutId)
      if (bundlerGasPrices) {
        this.gasPrices = bundlerGasPrices as GasSpeeds
        this.updatedAt = Date.now()
        this.#lastVisibleErrorAt = undefined

        this.emitUpdate()
        return
      }
    }

    // fallback to our gas price fetch if:
    // * all bundlers on the networks are not working or there are no bundlers
    // * we're doing a bundler estimate so we'd have a fallback option
    const gasPriceData = await getGasPriceRecommendations(this.#provider, this.#network, -1, () => {
      return !this.#getSignAccountOpState().stopRefetching
    }).catch((e) => {
      const signAccountOpState = this.#getSignAccountOpState()
      // null because the estimation is destroyed with signAccountOp
      const estimation = signAccountOpState.estimation as EstimationController | null

      // if the gas price data has been fetched once successfully OR an estimation error
      // is currently being displayed, do not emit another error
      if (this.gasPrices || !estimation || estimation.estimationRetryError) return

      // getGasPriceRecommendations rejects with 'operation aborted' as soon as the
      // signAccountOp stops refetching (reject / pause / destroy). That's us tearing
      // the fetch down, not the network failing — surfacing it would show a fee error
      // at the exact moment the user walks away from the request.
      if (signAccountOpState.stopRefetching) return

      const { type } = decodeError(e)

      let message = "We couldn't retrieve the latest network fee information."

      if (type === ErrorType.ConnectivityError) {
        message = 'Network connection issue prevented us from retrieving the current network fee.'
      }

      this.emitError({
        ...this.#getFailureEmitOptions(emitLevelOnFailure),
        message,
        error: new Error(`Failed to fetch gas price on ${this.#network.name}: ${e?.message}`)
      })
      return null
    })

    if (gasPriceData && gasPriceData.gasPrice) {
      this.gasPrices = gasPriceToBundlerFormat(gasPriceData.gasPrice)
      this.#lastVisibleErrorAt = undefined
    }
    this.updatedAt = Date.now()

    this.emitUpdate()
  }

  destroy() {
    super.destroy()
  }
}
